// Permission category metadata for the Edit Role page.
// Extracted from EditRolePage.tsx (Session 4 decomposition) — data is unchanged.
export interface Permission {
  id: string;
  name: string;
  description: string;
}

export interface PermissionGroup {
  name: string;
  permissions: Permission[];
}

export interface PermissionCategory {
  id: string;
  name: string;
  description: string;
  iconName: 'LayoutDashboard' | 'Clock' | 'FileText' | 'CheckSquare' | 'BarChart3' | 'Settings';
  iconColor: string;
  scope: 'basic' | 'management' | 'admin' | 'advanced';
  permissions: Permission[];
  groups?: PermissionGroup[];
}

export const permissionCategories: PermissionCategory[] = [
  {
    id: 'dashboard',
    name: 'Dashboard Permissions',
    description: 'Access to dashboard views and metrics',
    iconName: 'LayoutDashboard',
    iconColor: '',
    scope: 'basic',
    permissions: [
      { id: 'dashboard.view_own', name: 'View Own Dashboard', description: 'View personal dashboard with own metrics and action items' },
      { id: 'dashboard.view_team', name: 'View Team Dashboard', description: 'View team dashboard with team member metrics' },
      { id: 'dashboard.view_department', name: 'View Department Dashboard', description: 'View department-wide dashboard with aggregated metrics' },
    ],
  },
  {
    id: 'timesheet',
    name: 'Timesheet Permissions',
    description: 'Manage timesheet entry and tracking capabilities',
    iconName: 'Clock',
    iconColor: '',
    scope: 'basic',
    groups: [
      {
        name: 'View Access',
        permissions: [
          { id: 'timesheet.view_own', name: 'View Own Timesheets', description: 'View and access personal timesheet entries' },
          { id: 'timesheet.view_team', name: 'View Team Timesheets', description: 'View timesheet entries for direct reports' },
          { id: 'timesheet.view_all', name: 'View All Timesheets', description: 'View any employee\'s timesheet entries' },
        ],
      },
      {
        name: 'Entry Management',
        permissions: [
          { id: 'timesheet.create', name: 'Create Time Entries', description: 'Add new time entries to timesheets' },
          { id: 'timesheet.edit_own', name: 'Edit Own Entries', description: 'Modify personal time entries (status-dependent)' },
          { id: 'timesheet.edit_team', name: 'Edit Team Entries', description: 'Modify team member time entries (limited)' },
          { id: 'timesheet.edit_all', name: 'Edit All Entries', description: 'Modify any timesheet entry (admin override)' },
          { id: 'timesheet.delete_own', name: 'Delete Own Entries', description: 'Remove personal time entries' },
          { id: 'timesheet.delete_team', name: 'Delete Team Entries', description: 'Remove team member time entries' },
        ],
      },
      {
        name: 'Workflow Actions',
        permissions: [
          { id: 'timesheet.submit', name: 'Submit Timesheet', description: 'Submit timesheet for approval' },
          { id: 'timesheet.recall', name: 'Recall Timesheet', description: 'Recall a submitted timesheet' },
          { id: 'timesheet.copy', name: 'Copy Timesheet', description: 'Copy entries from previous or selected weeks' },
          { id: 'timesheet.view_audit', name: 'View Audit History', description: 'View timesheet change history and audit trail' },
        ],
      },
    ],
    permissions: [],
  },
  {
    id: 'approvals',
    name: 'Approval Permissions',
    description: 'Manage timesheet approval workflows',
    iconName: 'CheckSquare',
    iconColor: '',
    scope: 'management',
    permissions: [
      { id: 'approvals.view_queue', name: 'View Approval Queue', description: 'Access the approval dashboard and pending queue' },
      { id: 'approvals.approve', name: 'Approve Timesheets', description: 'Approve submitted timesheet entries' },
      { id: 'approvals.reject', name: 'Reject Timesheets', description: 'Reject timesheets with reason' },
      { id: 'approvals.bulk_approve', name: 'Bulk Approve & Reject', description: 'Approve or reject multiple timesheets at once' },
      { id: 'approvals.view_details', name: 'View Approval Details', description: 'View detailed timesheet information during approval' },
      { id: 'approvals.view_automation', name: 'View Automation Suggestions', description: 'View AI-powered automation suggestions' },
      { id: 'approvals.manage_automation', name: 'Manage Automation Rules', description: 'Configure and manage approval automation rules' },
    ],
  },
  {
    id: 'reports',
    name: 'Reports & Analytics Permissions',
    description: 'Access reporting and analytics features',
    iconName: 'BarChart3',
    iconColor: '',
    scope: 'management',
    groups: [
      {
        name: 'Standard Reports',
        permissions: [
          { id: 'reports.view_own', name: 'View Own Reports', description: 'View personal timesheet and utilization reports' },
          { id: 'reports.view_team', name: 'View Team Reports', description: 'View reports for direct reports' },
          { id: 'reports.view_department', name: 'View Department Reports', description: 'View reports for entire department' },
          { id: 'reports.view_all', name: 'View All Reports', description: 'View organization-wide reports' },
          { id: 'reports.export', name: 'Export Reports', description: 'Export report data to CSV, Excel, or PDF' },
        ],
      },
    ],
    permissions: [],
  },
  {
    id: 'team',
    name: 'Team Management Permissions',
    description: 'Manage team members and their details',
    iconName: 'Settings',
    iconColor: '',
    scope: 'management',
    permissions: [
      { id: 'team.view_directory', name: 'View Employee Directory', description: 'View the employee directory and basic information' },
      { id: 'team.view_details', name: 'View Employee Details', description: 'View detailed employee information and profiles' },

    ],
  },
  {
    id: 'projects',
    name: 'Project Management Permissions',
    description: 'Control project and task management access',
    iconName: 'FileText',
    iconColor: '',
    scope: 'management',
    permissions: [
      { id: 'projects.view_assigned', name: 'View Assigned Projects', description: 'View only projects assigned to the user' },
      { id: 'projects.view_all', name: 'View All Projects', description: 'View all active and archived projects' },
      { id: 'projects.create', name: 'Create Projects', description: 'Create new projects' },
      { id: 'projects.edit', name: 'Edit Projects', description: 'Modify project details and settings' },
      { id: 'projects.delete', name: 'Delete Projects', description: 'Delete projects (with restrictions)' },
      { id: 'projects.archive', name: 'Archive Projects', description: 'Archive completed or inactive projects' },
      { id: 'projects.assign', name: 'Assign Projects', description: 'Assign projects to team members' },
      { id: 'projects.manage_budget', name: 'Manage Project Budgets', description: 'View and manage project budgets and forecasts' },
    ],
  },
  {
    id: 'integrations',
    name: 'Integration Permissions',
    description: 'Manage system integrations and company data',
    iconName: 'Settings',
    iconColor: '',
    scope: 'admin',
    permissions: [
      { id: 'integrations.view', name: 'View Integrations', description: 'View integration configurations and status' },
      { id: 'integrations.manage', name: 'Manage Integrations', description: 'Configure and manage system integrations' },
      { id: 'integrations.sync', name: 'Sync Data', description: 'Trigger manual data synchronization' },
      { id: 'companies.view', name: 'View Companies', description: 'View company list and basic information' },
      { id: 'companies.manage', name: 'Manage Companies', description: 'Create, edit, and manage company settings' },
    ],
  },
  {
    id: 'settings',
    name: 'Settings & Administration Permissions',
    description: 'Configure system settings and manage users',
    iconName: 'Settings',
    iconColor: '',
    scope: 'admin',
    permissions: [
      { id: 'settings.view', name: 'View Settings', description: 'View all settings (read-only access)' },
      { id: 'settings.manage_general', name: 'Manage General Settings', description: 'Configure general application settings' },

      { id: 'settings.manage_deadlines', name: 'Manage Deadlines', description: 'Configure timesheet submission deadlines' },
      { id: 'settings.manage_policies', name: 'Manage Policy Settings', description: 'Configure policy violation rules and monitoring' },
      { id: 'settings.manage_utilization', name: 'Manage Utilization Settings', description: 'Configure utilization targets and thresholds' },
      { id: 'settings.manage_roles', name: 'Manage Roles & Permissions', description: 'Create and manage user roles and permissions' },
      { id: 'settings.manage_approval_workflows', name: 'Manage Approval Workflows', description: 'Configure approval workflows and stages' },
      { id: 'settings.manage_notifications', name: 'Manage Notifications', description: 'Configure notification preferences and rules' },
      { id: 'settings.view_audit', name: 'View Audit Logs', description: 'View system audit logs and change history' },
      { id: 'settings.manage_security', name: 'Manage Security Settings', description: 'Manage security and access control settings' },
    ],
  },
  {
    id: 'data',
    name: 'Data Operations Permissions',
    description: 'Import, export, and bulk data operations',
    iconName: 'Settings',
    iconColor: '',
    scope: 'admin',
    permissions: [
      { id: 'data.import', name: 'Import Data', description: 'Import timesheet and employee data' },
      { id: 'data.export', name: 'Export Data', description: 'Export data to various formats' },
      { id: 'data.bulk_operations', name: 'Bulk Operations', description: 'Perform bulk data operations' },
    ],
  },
];
